import * as React from 'react';
import { createUseStyles } from 'react-jss';
import { Theme } from 'helpers/theme';
import {
  ErrorText,
  Heading,
  SecondaryText,
  SecondaryTitle
} from 'components/core/Typography';
import type { SectionName } from '../AutomatedCheckModal';
import Spacer from 'sharedComponents/core/Spacers/Spacer';
import UploadFiles, { FileUpload } from '../UploadFiles';
import Button from 'components/Button/Button';
import moment from 'moment';

const useStyles = createUseStyles((theme: Theme) => ({
  divider: {
    width: '100%',
    height: 1,
    background: '#a4a4a442',
    margin: '30px 0px'
  },
  buttons: {
    display: 'flex',
    alignItems: 'center',
    gap: '20px',
    justifyContent: 'flex-end'
  },
  dateInput: {
    border: '1px solid #e7e7e7',
    padding: 10,
    borderRadius: 6
  },
  select: {
    gap: '16px',
    margin: '5px 0px',
    display: 'flex',
    borderRadius: 8,
    padding: 15,
    '&:hover': {
      cursor: 'pointer',
      background: '#f6f7fa'
    }
  },
  selectID: {
    extend: 'select',
    flexDirection: 'column'
  },
  label: {
    fontSize: 15,
    fontWeight: 400
  }
}));

type Props = {
  sectionName: SectionName;
  delegateUUID: string;
  verificationID?: string;
  files: FileUpload[];
  idDate: moment.Moment | undefined;
  onIDDate: (date: moment.Moment) => void;
  idApproved: boolean;
  onIDApproved: (approved: boolean) => void;
  onUpdateFiles: (files: FileUpload[]) => void;
  onNext: () => void;
  onPrev: () => void;
};

function ID({
  sectionName,
  delegateUUID,
  verificationID,
  idDate,
  onIDDate,
  idApproved,
  onIDApproved,
  files,
  onUpdateFiles,
  onNext,
  onPrev
}: Props) {
  const classes = useStyles();
  return (
    <>
      <Heading>ID upload</Heading>
      <SecondaryText>
        Upload candidate ID document - i.e passport. <br />
        <br /> Note: Birth certificates will require further checking.
      </SecondaryText>
      <>
        <Spacer vertical spacing={2} />
        <UploadFiles
          delegateUUID={delegateUUID}
          automatedVerificationID={verificationID}
          fileStage={sectionName}
          files={files}
          onUpdateFiles={onUpdateFiles}
          title="Drag or click to upload"
          description={
            <>
              Supported files: .PDF, .JPG, .PNG
              <br />
            </>
          }
        />
      </>
      <Spacer vertical spacing={1} />
      <div className={classes.divider} />
      {files.length > 0 && (
        <>
          <div className={classes.selectID}>
            <div className={classes.label}>
              <SecondaryTitle>ID expiry date</SecondaryTitle>
              <SecondaryText>
                The date of expiry for this ID document. Leave blank for birth
                certificates
              </SecondaryText>
            </div>
            <input
              type="date"
              className={classes.dateInput}
              value={idDate?.format('YYYY-MM-DD')}
              onChange={(e) => {
                onIDDate(moment.utc(e.target.value, 'YYYY-MM-DD'));
              }}
            />
          </div>
          <div
            className={classes.select}
            onClick={() => {
              onIDApproved(!idApproved);
            }}
          >
            <div className={classes.label}>
              <SecondaryTitle>
                ID document was verified in person
              </SecondaryTitle>
              <SecondaryText>
                I confirm that the uploaded document was verified in person
              </SecondaryText>
            </div>
            <input
              type="checkbox"
              checked={idApproved}
              onChange={(e) => {
                onIDApproved(e.target.checked);
              }}
            />
          </div>
        </>
      )}

      <div className={classes.divider} />
      {/* <ErrorText style={{ textAlign: 'right' }}>{errorText}</ErrorText> */}
      <Spacer vertical spacing={1} />
      <div className={classes.buttons}>
        <Button
          archetype="flat"
          onClick={() => {
            onPrev();
          }}
        >
          Back
        </Button>
        <Button
          archetype="flatPrimary"
          disabled={files.length === 0 || !idApproved}
          onClick={() => {
            onNext();
          }}
        >
          Next
        </Button>
      </div>
    </>
  );
}

export default ID;
